import { useEffect, useState } from "react"
import { Comment } from "@nova/schemas"
import { GenericInspectorPanelContainer } from "../inspector-panels/GenericInspectorPanel"
import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useComments, useCreateComment, useMarkThreadRead } from "features/comments/Comments"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { MapCommentThreadCard } from "./MapCommentThreadCard"
import { CommentMoreMenu } from "./CommentMoreMenu"
import { CommentInput } from "plugins/Toolbox/MapComments/CommentInput"
import { useCommentImageUpload } from "plugins/Toolbox/MapComments/useCommentImageUpload"
import { backToMapCommentList, closeMapComment } from "./module"
import { IconButton } from "@nova/component-btn"

const authorGroupingWindowMs = 2 * 60 * 1000

const isGroupedWithPrevious = (
    comment: Comment<"map-pin" | "map-comment">,
    previous: Comment<"map-pin" | "map-comment"> | undefined
) =>
    !!previous &&
    previous.createdBy === comment.createdBy &&
    comment.createdAt - previous.createdAt < authorGroupingWindowMs

export const MapCommentInspectorPanel = ({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const { comments: [rootComment] = [] } = useComments<"map-pin">({ entityId: id })
    const { comments: replies = [] } = useComments<"map-comment">({
        entityId: rootComment?.id ?? "",
    })
    const { createComment, isMutating } = useCreateComment()
    const { markThreadRead } = useMarkThreadRead()
    const [reply, setReply] = useState("")
    const imageUpload = useCommentImageUpload(rootComment?.id ?? "")

    const unreadRootId = rootComment?.isUnread ? rootComment.id : undefined

    useEffect(() => {
        if (unreadRootId) markThreadRead({ rootCommentId: unreadRootId })
    }, [unreadRootId, markThreadRead])

    const submitReply = async () => {
        if (!rootComment) return
        const submitted = reply
        setReply("")
        try {
            await createComment({
                comment: submitted,
                context: { target: "map-comment" },
                entityId: rootComment.id,
                imageFileIds: imageUpload.uploadedFileIds,
            })
            imageUpload.reset()
        } catch {
            setReply(submitted)
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to post reply. Try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    return (
        <GenericInspectorPanelContainer>
            <div className="flex flex-col min-h-0 border-b border-white-200 pt-4 pb-3 px-4">
                <div className="flex items-center justify-between">
                    <IconButton
                        className="flex items-center gap-1 text-gray-100"
                        onClick={backToMapCommentList}
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        <small>Back</small>
                    </IconButton>
                    <div className="flex items-center gap-1">
                        {rootComment && (
                            <CommentMoreMenu
                                comment={rootComment}
                                mapEntityId={id}
                                onDelete={backToMapCommentList}
                            />
                        )}
                        <Icons.SmallIcon
                            Icon={Icons.XClose}
                            className="text-gray-300 hover:text-gray-0 cursor-pointer"
                            onClick={closeMapComment}
                        />
                    </div>
                </div>
            </div>
            <ScrollableArea className="flex flex-col gap-4 px-2 py-3">
                {rootComment && (
                    <MapCommentThreadCard
                        comment={rootComment}
                        mapEntityId={id}
                        showMoreMenu={false}
                        onDelete={backToMapCommentList}
                    />
                )}
                {replies.map((reply, index) => (
                    <MapCommentThreadCard
                        key={reply.id}
                        comment={reply}
                        mapEntityId={id}
                        showAuthor={
                            !isGroupedWithPrevious(reply, replies[index - 1] ?? rootComment)
                        }
                    />
                ))}
            </ScrollableArea>
            <div className="px-2 pb-2">
                <CommentInput
                    value={reply}
                    onChange={setReply}
                    onSubmit={submitReply}
                    disabled={isMutating || !rootComment}
                    imageUpload={imageUpload}
                />
            </div>
        </GenericInspectorPanelContainer>
    )
}
